import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, getToken, formatApiError } from "@/lib/api";
import { clearPwaApiCache } from "@/lib/pwa";
import { clearQueue } from "@/lib/offlineQueue";
import { unsubscribeFromPush } from "@/lib/pushNotifications";

const AuthContext = createContext(null);

export const applyTheme = (t) => {
  document.documentElement.classList.toggle("dark", t === "dark");
  try { localStorage.setItem("theme_pref", t); } catch { /* ignore */ }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      applyTheme(data.theme_pref || "light");
    } catch (e) {
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    try { applyTheme(localStorage.getItem("theme_pref") || "light"); } catch { /* ignore */ }
    refresh();
  }, [refresh]);

  const login = async (identifier, password) => {
    setError("");
    try {
      // Backend accepts `username` or `email`. If the identifier contains `@`,
      // send as email; otherwise as username.
      const body = String(identifier).includes("@")
        ? { email: identifier, password }
        : { username: identifier, password };
      const { data } = await api.post("/auth/login", body);
      setToken(data.token);
      setUser(data.user);
      applyTheme(data.user?.theme_pref || "light");
      // Flag consumed by <DutyLoginAlert /> — shows the "Start duty?" prompt
      // once per login for users who opted in via Profile.
      try { if (data.user?.duty_alert_enabled) sessionStorage.setItem("st_duty_alert_pending", "1"); } catch { /* ignore */ }
      return data.user;
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    // Best-effort: unsubscribe push BEFORE we drop the token so the backend
    // can honour the DELETE request.
    try { await unsubscribeFromPush(); } catch { /* ignore */ }
    try { await api.post("/auth/logout"); } catch { /* ignore — backend may be offline */ }
    setToken(null);
    setUser(null);
    // Purge cached API reads + any pending queued mutations so the next user
    // on a shared device doesn't see (or replay) the previous user's data.
    clearPwaApiCache();
    clearQueue();
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, error, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
